import { ChangeEvent, Dispatch, MouseEvent, ReactNode, SetStateAction, useEffect, useRef, useState } from "react"
import HeaderCell from "./CellTypes/HeaderCell";
import TextCell from "./CellTypes/TextCell";
import NestedDropdown from "../Menus&Dropdowns/NestedDropdown";
import Dropdown, { DropdownOption } from "../Menus&Dropdowns/Dropdown";
import { useSelector } from "react-redux";
import { selectNav } from "../../store/selectors/nav.selector";
import StructureCell from "./CellTypes/StructureCell";
import NoResultsImage from '../../assets/Images/noResultsFound.png';
import { ReactComponent as MlclIcon } from '../../assets/Icons/adornments/molecule-thin.svg';

import { convertNumberToDecimal } from "../../utils/formattingFns";
import StatsCell from "./CellTypes/StatsCell";
import BooleanCell from "./CellTypes/BooleanCell";
import StatusCell from "./CellTypes/StatusCell";
import CustomCheckbox from "../Form/CustomCheckbox";
import TagCell from "./CellTypes/TagCell";
import RolesCell from "./CellTypes/RolesCell";
import { Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DetailsModal from "../Popups/DetailsModal";


export interface TableHeader {
    label: string,
    value: string,
    isShown: boolean,
    alignment: 'left' | 'center',
    columnMetadata?: ReactNode,
}

export interface TableDataField {
    associatedColumn: string,
    value: string | string[] | any[],
    type: 'text' | 'structure' | 'tags' | 'plot' | 'stat' | 'boolean' | 'status' | 'roles' | 'details',
    alignment: 'left' | 'center',
    action?: (e:MouseEvent<HTMLButtonElement>) => void,
}

export interface TableData {
    id: string | number,
    fields: TableDataField[],
    options?: DropdownOption[],
    isGroupHeader?: boolean,
    isVersionRow?: boolean,
    groupCount?: number,
}

export default function DataTable({
    isLoading,
    headers,
    tableData,
    paramValues,
    selection,
    statsType,
    threshold,
    sorting,
    setPopupMessage,
    moleculeHighlightingModelFamily,
}: {
    isLoading: boolean,
    headers: TableHeader[],
    tableData: TableData[],
    paramValues?: any[],
    selection?: {
        selectedItems: (string | number)[],
        setSelectedItems: Dispatch<SetStateAction<(string | number)[]>>,
    },
    statsType?: 'model' | 'prediction',
    threshold?: number,
    sorting?: {
        sortableHeaders: string[],
        sortHeaders: (e: MouseEvent<HTMLButtonElement>, value: string) => void,
        sortByParam: string | null,
        sortOrderParam: 'desc' | 'asc' | null,
    },
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void,
    moleculeHighlightingModelFamily?: number | null,
}) {
    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const [ isScrollable, setIsScrollable ] = useState(false);
    const [ atLeft, setAtLeft ] = useState(true);
    const [ atRight, setAtRight ] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const tableContainer = tableContainerRef.current;

        const checkScroll = () => {
            if (!tableContainer) return;
            const { scrollLeft, scrollWidth, clientWidth } = tableContainer;
            setIsScrollable(scrollWidth > clientWidth);
            setAtLeft(scrollLeft === 0);
            setAtRight(scrollLeft + clientWidth >= scrollWidth - 1);
        }

        checkScroll();

        const resizeObserver = new ResizeObserver(checkScroll);
        if (tableContainer) {
            resizeObserver.observe(tableContainer);
        }

        tableContainer?.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);

        return () => {
            resizeObserver.disconnect();
            tableContainer?.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        }
    }, []);

    const isNavOpen = useSelector(selectNav);

    const handleItemSelection = (e:ChangeEvent<HTMLInputElement>) => {
        if (selection) {
            const { selectedItems, setSelectedItems } = selection;
            const id = e.target.value;
            const isNum = typeof tableData[0].id === 'number';

            if (e.target.checked) {
                setSelectedItems(selectedItems.concat(isNum ? Number(id) : id));
            } else {
                setSelectedItems(selectedItems.filter(item => item !== (isNum ? Number(id) : id)));
            }
        }
    };

    const handleAllItemSelection = (e:ChangeEvent<HTMLInputElement>) => {
        if (selection) {
            const { setSelectedItems } = selection;
            if (e.target.checked) {
                setSelectedItems(tableData.map(data => data.id))
            } else {
                setSelectedItems([]);
            }
        }
    }

    const changeTableWidth = () => {
        if (isNavOpen) {
            return 'table-container-expanded';
        } else {
            return 'table-container';
        }
    }

    const navigateToMoleculeHighlightingPage = (data: TableData) => {
        const fields = data.fields;
        if (fields.length > 0) {
            const highlightingField = fields.find(field => field.associatedColumn === 'molecule_highlighting');

            if (highlightingField) {
                navigate(`prediction/${data.id}/${encodeURIComponent(highlightingField.value as string)}`)
            }
        }
    }
    

    return (
        <div ref={tableContainerRef} className={`${changeTableWidth()} w-full transition-all grow overflow-y-auto overflow-x-auto bg-white rounded-xl border border-outline`}>
            <table className="w-full">
                <thead>
                {isLoading ? (
                    <tr className='bg-secondary text-default relative sticky top-0 z-30'>
                        {Array.from(Array(6).keys()).map(key => (
                            <td key={key} className="py-4 px-6"><Skeleton variant='text' sx={{fontSize: '1rem'}} width='100%'/></td>
                        ))}
                    </tr>
                ) : (
                    <tr className='bg-secondary text-default relative sticky top-0 z-30'>
                        <>
                        {selection && (
                            <th className="bg-secondary sticky left-0 z-40">
                                <div className="py-4 px-6 flex justify-start items-center">
                                    <CustomCheckbox
                                        indeterminate={(selection.selectedItems.length !== tableData.length) && selection.selectedItems.length > 0}
                                        checked={selection.selectedItems.length === tableData.length}
                                        handleChange={handleAllItemSelection}
                                    />
                                </div>
                            </th>
                        )}
                        {headers.find(header => header.value === 'molecule_highlighting') && (
                            <th className="bg-secondary sticky left-0 z-40">
                                <div className="w-[58px]"></div>
                            </th>
                        )}
                        {headers.filter(header => header.value !== 'molecule_highlighting').map((header, headerIndex) => {
                            if (header.isShown) {
                                return (
                                    <th 
                                        key={headerIndex} 
                                        className={`bg-secondary relative ${headerIndex === 0 ? (selection || headers.find(header => header.value === 'molecule_highlighting')) ? 'sticky left-[58px] z-40' : 'sticky left-0 z-40' : ''} ${headerIndex === 0 && isScrollable && !atLeft ? 'shadow-left' : ''}`}
                                    >
                                        {headerIndex === 0 || (headerIndex === headers.length-1 && tableData.length > 0 && !tableData[0].options) ? (
                                            <>
                                            {headerIndex === 0 && isScrollable && !atLeft ? (
                                                <div className="absolute top-0 left-0 right-0 bottom-0 p-1 shadow-left"></div>
                                            ) : (
                                                <></>
                                            )}
                                            </>
                                        ) : (
                                            <></>
                                        )}
                                        <HeaderCell
                                            header={header}
                                            sorting={sorting}
                                        />
                                    </th>
                                ) 
                            } else {
                                return null;
                            }
                        })}
                        {tableData.length > 0 && tableData[0].options && (
                            <th
                                className={`bg-secondary sticky relative right-0 z-40 ${isScrollable && !atRight ? 'shadow-right' : ''}`}
                            >
                                {isScrollable && !atRight && <div className="absolute top-[1px] left-0 right-0 bottom-[-1px] p-1 shadow-right"></div>}
                                <div className={`py-4 px-6`}></div>
                            </th>
                        )}
                        </>
                    </tr>
                )}
                </thead>
                <tbody>
                    {isLoading ? (
                    <>
                    {Array.from(Array(10).keys()).map(k => (
                        <tr key={k}>
                            {Array.from(Array(6).keys()).map(key => (
                                <td key={key} className="py-4 px-6"><Skeleton variant='text' width='100%' sx={{fontSize: '1rem'}}/></td>
                            ))}
                        </tr>
                    ))}
                    </>
                    ) : tableData.length > 0 ? (
                       <>
                        {tableData.map((data,i) => (
                            <tr key={i} className={`${selection && selection.selectedItems.includes(data.id) ? 'bg-[#D9DBE7]' : 'table-body'}`}>
                                {selection && (
                                    <td className={`${selection && selection.selectedItems.includes(data.id) && 'bg-[#D9DBE7]'} sticky left-0 z-20`}>
                                        <div className="py-6 px-6">
                                            <CustomCheckbox
                                                checked={selection.selectedItems.includes(data.id)}
                                                handleChange={handleItemSelection}
                                                value={data.id}
                                            />
                                        </div>
                                    </td>
                                )}
                                {headers.find(header => header.value === 'molecule_highlighting') && (
                                    <td className={`${selection && selection.selectedItems.includes(data.id) && 'bg-[#D9DBE7]'} sticky left-0 z-20`}>
                                        <div className="w-[58px] h-full flex justify-center items-center">
                                            {data.fields.find(field => field.associatedColumn === 'molecule_highlighting' && field.value !== '') && (
                                                <button 
                                                    onClick={(e:any) => navigateToMoleculeHighlightingPage(data)} 
                                                    className='w-[20px] text-disable hover:text-primary transition-all'
                                                >
                                                    <MlclIcon />
                                                </button>
                                            )}
                                            
                                        </div>
                                    </td>
                                )}
                                {headers.filter(header => header.value !== 'molecule_highlighting').map((header, headerIndex) => {
                                    const matchingData = data.fields.find(field => field.associatedColumn === header.value);
                                    if (matchingData && header.isShown) {
                                        if (matchingData.type === 'stat' && statsType !== undefined) {
                                            return (
                                                <StatsCell 
                                                    key={headerIndex}
                                                    value={matchingData.value as string}
                                                    name={matchingData.associatedColumn}
                                                    statsType={statsType}
                                                    isSelected={selection && selection.selectedItems.includes(data.id) ? true : false}
                                                    threshold={threshold}
                                                />
                                            )
                                        } else {
                                            return (
                                            <td 
                                                key={headerIndex}
                                                className={`${selection && selection.selectedItems.includes(data.id) && 'bg-[#D9DBE7]'} relative ${headerIndex === 0 ? (selection || headers.find(header => header.value === 'molecule_highlighting')) ? 'sticky left-[58px] z-20' : 'sticky left-0 z-20' : ''} ${headerIndex === 0 && isScrollable && !atLeft ? 'shadow-left' : ''}`}
                                            >
                                                {headerIndex === 0 || (headerIndex === headers.length-1 && tableData.length > 0 && !tableData[0].options) ? (
                                                    <>
                                                    {headerIndex === 0 && isScrollable && !atLeft ? (
                                                        <div className="absolute top-0 left-0 right-0 bottom-0 p-1 shadow-left"></div>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    </>
                                                ) : (
                                                    <></>
                                                )}
                                                {matchingData.type === 'structure' && (header.label.toLocaleLowerCase().includes('structure') || header.label.toLocaleLowerCase().includes('compound')) ? (
                                                    <StructureCell
                                                        index={headerIndex}
                                                        data={data}
                                                        value={matchingData.value as string}
                                                        threshold={threshold}
                                                        setPopupMessage={setPopupMessage}
                                                        moleculeHighlightingModelFamily={moleculeHighlightingModelFamily}
                                                        id={`molecule-structure-svg-${data.id}`}
                                                        hasPadding={true}
                                                    />
                                                ) : matchingData.type === 'tags' ? (
                                                    <TagCell 
                                                        value={matchingData.value as string[]}
                                                        label={header.label}
                                                        hasPadding
                                                    />
                                                ) : matchingData.type === 'boolean' ? (
                                                    <BooleanCell 
                                                        value={matchingData.value as string}
                                                    />
                                                ) : matchingData.type === 'status' ? (
                                                    <StatusCell 
                                                        value={matchingData.value as string}
                                                    />
                                                ) : matchingData.type === 'roles' ? (
                                                    <RolesCell 
                                                        value={matchingData.value as string[]}
                                                    />
                                                ) : matchingData.type === 'text' ? (
                                                    <TextCell 
                                                        value={convertNumberToDecimal(matchingData.value as string | number)}
                                                        alignment={matchingData.alignment}
                                                        action={matchingData.action}
                                                        headerIndex={headerIndex}
                                                        hasPadding
                                                    />
                                                ) : (
                                                    <div className={`flex justify-start items-center gap-2 py-4 px-6`}>
                                                        <DetailsModal 
                                                            data={data}
                                                            isGridItem={false}
                                                            label={matchingData.value as string}
                                                            setPopupMessage={setPopupMessage}
                                                        />
                                                    </div>
                                                )}
                                            </td>
                                            )
                                        }
                                        
                                    } else {
                                        return null;
                                    }
                                })}
                                {data.options && (
                                    <td
                                        className={`${selection && selection.selectedItems.includes(data.id) && 'bg-[#D9DBE7]'} sticky relative right-0 z-20 ${isScrollable && !atRight ? 'shadow-right' : ''}`}
                                    >
                                        {isScrollable && !atRight && <div className="absolute top-[-1px] left-0 right-0 bottom-[-1px] p-1 shadow-right"></div>}
                                        <div className="py-4 px-6 flex justify-end items-center relative z-30">
                                            {data.options.find(opt => opt.moreOptions !== undefined) ? (
                                                <NestedDropdown 
                                                    options={data.options}
                                                    dir="left"
                                                />
                                            ) : (
                                                <Dropdown  
                                                    options={data.options}
                                                />
                                            )}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                        </>
                ) : paramValues !== undefined && paramValues.length > 0 ? (
                    <tr>
                        <td colSpan={headers.length+1} className="h-[400px]">
                            <div className="w-full flex flex-col justify-center items-center gap-6 text-default">
                                <img src={NoResultsImage} className="w-[120px]" alt='no-results-found'/>
                                <div className="flex flex-col gap-3 justify-center items-center">
                                <p className="text-[20px] font-semibold text-disable">No results found</p>
                                <p className="text-disable font-normal">Adjust your filters and try again.</p>
                                </div>
                            </div>
                        </td>
                    </tr>
                ) : (
                    <tr>
                        <td colSpan={headers.length} rowSpan={10}></td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>

    )
}